/**
 * useArcadeScore — the shared score / combo / flash state machine for the
 * monograph mini-games.
 *
 * Every arcade panel was hand-rolling the same four pieces of presentation
 * state — running score, current combo, a flash token, and a flash color — plus
 * the same "a hit bumps the combo and adds combo-scaled points; a miss breaks
 * the combo" logic. This hook owns that once, as a small reducer, so each game
 * just calls `hit()` / `miss()` / `flash()` / `reset()` and reads `score`,
 * `combo`, `rank`, `flashToken`, and `flashColor`.
 *
 * Presentation only: it never decides a structural verdict. The pure model
 * decides what happened; the panel translates that into hit/miss/flash here.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { useCallback, useMemo, useReducer } from "react";
import {
  pointsForHit,
  rankForScore,
  type ArcadeRank,
} from "./arcadeScoring.js";

interface ScoreState {
  readonly score: number;
  readonly combo: number;
  readonly flashToken: number;
  readonly flashColor: string;
}

type ScoreAction =
  | { readonly kind: "hit"; readonly bonus: number; readonly color: string }
  | { readonly kind: "miss"; readonly color: string }
  | { readonly kind: "flash"; readonly color: string }
  | { readonly kind: "clearCombo" }
  | { readonly kind: "reset" };

function scoreReducer(state: ScoreState, action: ScoreAction): ScoreState {
  switch (action.kind) {
    case "hit": {
      const combo = state.combo + 1;
      return {
        score: state.score + pointsForHit(combo) + action.bonus,
        combo,
        flashToken: state.flashToken + 1,
        flashColor: action.color,
      };
    }
    case "miss":
      return {
        ...state,
        combo: 0,
        flashToken: state.flashToken + 1,
        flashColor: action.color,
      };
    case "flash":
      return {
        ...state,
        flashToken: state.flashToken + 1,
        flashColor: action.color,
      };
    case "clearCombo":
      return {
        ...state,
        combo: 0,
      };
    case "reset":
      return {
        score: 0,
        combo: 0,
        flashToken: 0,
        flashColor: state.flashColor,
      };
  }
}

export interface ArcadeScoreOptions {
  /** Default flash color for a hit. */
  readonly hitColor: string;
  /** Default flash color for a miss. */
  readonly missColor: string;
}

export interface ArcadeScore {
  readonly score: number;
  readonly combo: number;
  readonly rank: ArcadeRank;
  /** Monotonic token to feed {@link FlashOverlay}. */
  readonly flashToken: number;
  readonly flashColor: string;
  /** Register a successful move: +1 combo, combo-scaled points (+bonus), flash. */
  readonly hit: (options?: {
    readonly bonus?: number;
    readonly color?: string;
  }) => void;
  /** Register a broken move: combo resets to 0, flash in the miss color. */
  readonly miss: (color?: string) => void;
  /** Flash without changing score or combo. */
  readonly flash: (color: string) => void;
  /** Clear only the combo streak, preserving score and flash state. */
  readonly clearCombo: () => void;
  /** Reset score and combo to zero. */
  readonly reset: () => void;
}

export function useArcadeScore(options: ArcadeScoreOptions): ArcadeScore {
  const { hitColor, missColor } = options;
  const [state, dispatch] = useReducer(scoreReducer, {
    score: 0,
    combo: 0,
    flashToken: 0,
    flashColor: hitColor,
  });

  const hit = useCallback(
    (opts?: { readonly bonus?: number; readonly color?: string }): void => {
      dispatch({
        kind: "hit",
        bonus: opts?.bonus ?? 0,
        color: opts?.color ?? hitColor,
      });
    },
    [hitColor],
  );
  const miss = useCallback(
    (color?: string): void => {
      dispatch({ kind: "miss", color: color ?? missColor });
    },
    [missColor],
  );
  const flash = useCallback((color: string): void => {
    dispatch({ kind: "flash", color });
  }, []);
  const clearCombo = useCallback((): void => {
    dispatch({ kind: "clearCombo" });
  }, []);
  const reset = useCallback((): void => {
    dispatch({ kind: "reset" });
  }, []);

  return useMemo(
    (): ArcadeScore => ({
      score: state.score,
      combo: state.combo,
      rank: rankForScore(state.score),
      flashToken: state.flashToken,
      flashColor: state.flashColor,
      hit,
      miss,
      flash,
      clearCombo,
      reset,
    }),
    [state, hit, miss, flash, clearCombo, reset],
  );
}
